import React from "react";
import PropTypes from "prop-types";
import { Box, Typography, makeStyles } from "@material-ui/core";
import { useDroppable } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";
import ContentGridItem from "./ContentGridItem";

const useStyles = makeStyles((theme) => ({
  container: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    backgroundColor: "#ffffff",
  },
  grid: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: 200,
    color: theme.palette.text.secondary,
  },
  dropZone: {
    minHeight: 100,
    border: "2px dashed transparent",
    borderRadius: 8,
    transition: "all 0.2s ease",
  },
  dropZoneActive: {
    border: (props) => `2px dashed ${props.clientColor || "#1976d2"}`,
    backgroundColor: (props) => {
      const color = props.clientColor || "#1976d2";
      return `${color}0D`; // 0D = 5% opacity in hex
    },
  },
}));

const ContentGrid = ({
  folders,
  scenarios,
  selectedScenarioId,
  onFolderClick,
  onScenarioClick,
  onBackgroundClick,
  clientColor,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "rgm.cockpit.scenario_manager.content_grid",
  });
  const classes = useStyles({ clientColor });

  // Make the entire grid a drop zone for moving items to current folder
  const { setNodeRef, isOver } = useDroppable({
    id: "content-grid-drop-zone",
    data: { type: "grid" },
  });

  const hasItems = folders.length > 0 || scenarios.length > 0;

  // Handle click on background (not on items)
  const handleContainerClick = (e) => {
    if (e.target === e.currentTarget && onBackgroundClick) {
      onBackgroundClick();
    }
  };

  const handleGridClick = (e) => {
    if (e.target === e.currentTarget && onBackgroundClick) {
      onBackgroundClick();
    }
  };

  return (
    <Box
      ref={setNodeRef}
      className={`${classes.container} ${classes.dropZone} ${isOver ? classes.dropZoneActive : ""}`}
      onClick={handleContainerClick}
    >
      {hasItems ? (
        <Box className={classes.grid} onClick={handleGridClick}>
          {/* Render subfolders first */}
          {folders.map((folder) => (
            <ContentGridItem
              key={`folder-${folder.folder_id}`}
              type="folder"
              data={folder}
              isSelected={false}
              onClick={() => onFolderClick(folder.folder_id)}
              clientColor={clientColor}
            />
          ))}
          {/* Then render scenarios */}
          {scenarios.map((scenario) => (
            <ContentGridItem
              key={`scenario-${scenario.scenario_id}`}
              type="scenario"
              data={scenario}
              isSelected={selectedScenarioId === scenario.scenario_id}
              onClick={() => onScenarioClick(scenario.scenario_id)}
              clientColor={clientColor}
            />
          ))}
        </Box>
      ) : (
        <Box className={classes.empty}>
          <Typography variant="body1">{t("empty_title")}</Typography>
          <Typography variant="body2">{t("empty_hint")}</Typography>
        </Box>
      )}
    </Box>
  );
};

ContentGrid.propTypes = {
  folders: PropTypes.array.isRequired,
  scenarios: PropTypes.array.isRequired,
  selectedScenarioId: PropTypes.number,
  onFolderClick: PropTypes.func.isRequired,
  onScenarioClick: PropTypes.func.isRequired,
  onBackgroundClick: PropTypes.func,
  clientColor: PropTypes.string,
};

ContentGrid.defaultProps = {
  selectedScenarioId: null,
  clientColor: "#1976d2",
};

export default ContentGrid;
